import { useEffect, useRef, useState } from "react";
import { DefaultChatTransport, ToolUIPart } from "ai";
import { useChat } from "@ai-sdk/react";
import { ProviderModel } from "tokenlens";

import {
  PromptInput,
  PromptInputActionMenuContent,
  PromptInputActionMenu,
  PromptInputActionMenuTrigger,
  PromptInputBody,
  PromptInputFooter,
  PromptInputSelectItem,
  PromptInputSubmit,
  PromptInputTools,
  PromptInputActionAddAttachments,
  PromptInputButton,
  PromptInputSelect,
  PromptInputMessage,
  PromptInputSelectTrigger,
  PromptInputSelectValue,
  PromptInputSelectContent,
  PromptInputHeader,
  usePromptInputAttachments,
  PromptInputTextarea
} from "@/components/ui/ai/prompt-input";

import {
  Conversation,
  ConversationContent,
  ConversationEmptyState,
  ConversationScrollButton
} from "@/components/ui/ai/conversation";

import { Message, MessageContent, MessageResponse } from "@/components/ui/ai/message";

import { Tool, ToolHeader, ToolContent, ToolInput, ToolOutput } from "@/components/ui/ai/tool";
import { GlobeIcon, Rocket } from "lucide-react";
import { cn, okstatus } from "@/lib/utils";
import {
  Attachment,
  AttachmentPreview,
  AttachmentRemove,
  Attachments
} from "@/components/ui/ai/attachments";
import useSWR from "swr";
import { toast } from "@/components/ui/use-toast";
import logger from "@/lib/logger";
import { useLocalStorage } from "@/components/useLocalStorage";

const PromptInputAttachmentsDisplay = () => {
  const attachments = usePromptInputAttachments();

  if (attachments.files.length === 0) {
    return null;
  }

  return (
    <Attachments variant="inline">
      {attachments.files.map(attachment => (
        <Attachment
          data={attachment}
          key={attachment.id}
          onRemove={() => attachments.remove(attachment.id)}
        >
          <AttachmentPreview />
          <AttachmentRemove />
        </Attachment>
      ))}
    </Attachments>
  );
};

export const Chat = ({ id, onMessageSent }: { id: string; onMessageSent?: () => void }) => {
  const [useWebSearch, setUseWebSearch] = useState(false);
  const [text, setText] = useState("");
  const { data: ai } = useSWR<{
    agentId: string;
    modelProvider: string;
    defaultModelId: string;
    description: string;
    enableAttach: boolean;
    enableWebSearch: boolean;
    enableModelSelection: boolean;
    tools: Record<string, string>;
    workflows: Record<string, string>;
  }>("/api/ai");
  const { data: models } = useSWR<ProviderModel[]>(ai ? "/api/ai/models" : null);
  const [model, setModel] = useState<string | undefined>(undefined);
  const [previousModel, setPreviousModel] = useLocalStorage<string | undefined>(
    "ai-chat-model",
    undefined
  );

  useEffect(() => {
    if (models && models.length > 0) {
      if (previousModel && models.find(model => model.id === previousModel)) {
        setModel(previousModel);
      } else {
        setModel(ai?.defaultModelId ?? models[0].id);
      }
    }
  }, [models, previousModel, setPreviousModel, ai?.defaultModelId]);

  const [fetching, setFetching] = useState(true);
  const { messages, setMessages, sendMessage, status, stop } = useChat({
    id,
    transport: new DefaultChatTransport({
      api: `/api/ai/chat/${id}`
    }),
    experimental_throttle: 1000,
    onFinish: result => {
      logger.info("Chat finished", result);
      if (result.isError) {
        toast({
          title: "Error",
          description: result.finishReason ?? "Unknown error",
          variant: "destructive"
        });
      }
    }
  });

  const prevStatusRef = useRef(status);
  useEffect(() => {
    if (prevStatusRef.current !== "ready" && status === "ready" && onMessageSent) {
      onMessageSent();
    }
    prevStatusRef.current = status;
  }, [status, onMessageSent]);

  useEffect(() => {
    const fetchMessages = async (id: string) => {
      const data = await fetch(`/api/ai/chat/${id}`)
        .then(okstatus)
        .then(res => res.json());
      setMessages([...data]);
      setFetching(false);
    };

    setFetching(true);
    fetchMessages(id);
  }, [setMessages, id]);

  const handleSubmit = (message: PromptInputMessage) => {
    const hasText = Boolean(message.text);
    const hasAttachments = Boolean(message.files?.length);
    if (!hasText && !hasAttachments) {
      return;
    }

    sendMessage(
      {
        text: message.text || "Sent with attachments",
        files: message.files
      },
      {
        body: {
          provider: ai?.modelProvider,
          modelId: model,
          webSearch: useWebSearch
        }
      }
    );
    setText("");
  };

  if (!models) {
    return null;
  }

  return (
    <div className="flex flex-col flex-1 min-h-0">
      <Conversation>
        <ConversationContent
          className={cn(messages.length === 0 && status === "ready" && "h-full")}
        >
          {!fetching && messages.length === 0 && status === "ready" && (
            <ConversationEmptyState
              className="h-full"
              icon={
                <img src="/logo.svg" alt="DollarDeploy" className="w-10 h-10 dark:invert" />
              }
              title="How can I help you?"
              description={ai?.description}
            />
          )}

          {messages
            .filter(message =>
              message.parts?.find(
                part => part.type === "text" || part.type?.startsWith("tool-")
              )
            )
            .map(message => (
              <div key={message.id}>
                {message.parts?.map((part, i) => {
                  if (part.type === "text") {
                    return (
                      <Message key={`${message.id}-${i}`} from={message.role}>
                        <MessageContent>
                          <MessageResponse>{part.text}</MessageResponse>
                        </MessageContent>
                      </Message>
                    );
                  } else if (part.type?.startsWith("tool-")) {
                    const type = (part as ToolUIPart).type;
                    const title = type.startsWith("tool-workflow-")
                      ? ai?.workflows[type.replace("tool-workflow-", "")]
                      : ai?.tools[(part as ToolUIPart).type?.replace("tool-", "")];
                    const icon = type.startsWith("tool-workflow-") ? (
                      <img
                        src="/logo.svg"
                        alt="DollarDeploy"
                        className={cn(
                          "w-4 h-4 dark:invert",
                          status === "error" && "animate-none dark:invert"
                        )}
                      />
                    ) : (
                      <Rocket className="size-4" />
                    );
                    return (
                      <Tool key={`${message.id}-${i}`}>
                        <ToolHeader
                          type={(part as ToolUIPart).type}
                          title={title}
                          state={(part as ToolUIPart).state}
                          icon={icon}
                          className="cursor-pointer"
                        />
                        <ToolContent>
                          <ToolInput input={(part as ToolUIPart).input || {}} />
                          <ToolOutput
                            output={(part as ToolUIPart).output}
                            errorText={(part as ToolUIPart).errorText}
                          />
                        </ToolContent>
                      </Tool>
                    );
                  }

                  return null;
                })}
              </div>
            ))}

          {["submitted", "streaming", "error"].includes(status) && (
            <div className="flex items-center gap-2" aria-label="DollarDeploy is thinking">
              <img
                src="/logo.svg"
                alt="DollarDeploy"
                className={cn(
                  "w-5 h-5 dark:invert animate-pulse",
                  status === "error" && "animate-none dark:invert"
                )}
              />

              <span
                className={cn(
                  "text-sm text-muted-foreground",
                  status === "error" && "text-destructive"
                )}
              >
                {status === "error" ? "Error" : "Thinking"}
              </span>
            </div>
          )}
          <ConversationScrollButton />
        </ConversationContent>
      </Conversation>

      <PromptInput
        onSubmit={handleSubmit}
        globalDrop
        multiple
        className="border-0"
        accept="image/png,image/jpeg,application/pdf"
        maxFiles={2}
        maxFileSize={5 * 1024 * 1024}
        onError={error => {
          toast({
            title: "Error",
            description: error.message,
            variant: "destructive"
          });
        }}
      >
        <PromptInputHeader className="pb-0">
          <PromptInputAttachmentsDisplay />
        </PromptInputHeader>
        <PromptInputBody className="pb-0">
          <PromptInputTextarea
            className="pb-0 min-h-14 ring-0 border-0 focus-visible:ring-offset-0 focus-visible:ring-0"
            onChange={e => setText(e.target.value)}
            value={text}
            placeholder="Type your message..."
            disabled={status !== "ready"}
            autoFocus
          />
        </PromptInputBody>
        <PromptInputFooter>
          <PromptInputTools>
            {ai?.enableAttach && (
              <PromptInputActionMenu>
                <PromptInputActionMenuTrigger className="px-2 has-[>svg]:px-2" />
                <PromptInputActionMenuContent>
                  <PromptInputActionAddAttachments />
                </PromptInputActionMenuContent>
              </PromptInputActionMenu>
            )}
            {ai?.enableWebSearch && (
              <PromptInputButton
                onClick={() => setUseWebSearch(!useWebSearch)}
                tooltip={{ content: "Search the web", shortcut: "⌘K" }}
                variant={useWebSearch ? "default" : "ghost"}
              >
                <GlobeIcon size={16} />
                <span>Search</span>
              </PromptInputButton>
            )}

            {ai?.enableModelSelection && (
              <PromptInputSelect
                onValueChange={value => {
                  setModel(value);
                  setPreviousModel(value);
                }}
                value={model}
                key={models?.length + "_" + model}
              >
                <PromptInputSelectTrigger>
                  <PromptInputSelectValue />
                </PromptInputSelectTrigger>
                <PromptInputSelectContent>
                  {models?.map(model => (
                    <PromptInputSelectItem key={model.id} value={model.id}>
                      {model.name}
                    </PromptInputSelectItem>
                  )) ?? (
                    <PromptInputSelectItem value="**fetching**">
                      Fetching models...
                    </PromptInputSelectItem>
                  )}
                </PromptInputSelectContent>
              </PromptInputSelect>
            )}
          </PromptInputTools>
          <PromptInputSubmit
            onStop={stop}
            disabled={["submitted", "streaming"].includes(status) ? false : !text || !model}
            status={status}
          />
        </PromptInputFooter>
      </PromptInput>
    </div>
  );
};
